import React from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { KpiChart, fmtNum } from './charts';
import { fmtKpiValue, unitLabel, fmtStat } from './cockpitCfoModel';

/* Détail d'un indicateur — panneau INLINE dans la colonne droite (master-détail,
   plus de drawer superposé). Narratif différé : pas de verdict / « Pourquoi ? » —
   chiffres, stats, écart, graphique, cascade (EBITDA) et drivers (décomposition
   numérique réelle). ✕ = retour à la vue de repos (signaux + graphe clé). */
export default function KpiDrawer({ kpi, unit, onClose, onGoTab }) {
  const { t } = useTranslation();
  const p = useP();
  const div = unit === 'chf' ? 1 : 1000;

  const STATUS_LABEL = {
    favorable: p('Favorable'),
    attention: p('À surveiller'),
    critique: p('Alerte'),
  };

  const CHART_TITLE = {
    flow: p('Mensuel — réel / projection & moyenne mobile 12 mois glissants'),
    rate: p('Taux mensuel en % du CA net'),
    ebitda: p('EBITDA mensuel (absolu) & cumul'),
    caept: p('CA net 12 mois glissant & EPT mensuel'),
    vaept: p('Productivité — valeur ajoutée 12 mois glissants par EPT'),
    rendbars: p('VA/EPT & Coût/EPT — actuel vs 2025'),
    burnbars: p('Évolution du burn rate — charge mensuelle fixe'),
    scissors: p('Revenus & charges op. — 12 mois glissants (effet ciseaux)'),
    level: p('Historique mensuel & tendance'),
    liqproj: p('Solde de clôture · réel + prévisionnel'),
    runway: p('Projection : coûts fixes vs trésorerie'),
    stack: p('Composition BFRE — 12 mois glissants (réalisé)'),
    pills: p('Degrés de liquidité · curseur = référence'),
  };

  const stats = kpi.statsOverride
    ? kpi.statsOverride.map((s) => ({ lbl: s[0], val: s[1] }))
    : buildStats(kpi, unit, p);

  // Écart projection vs N-1 (CA net uniquement).
  let ecart = null;
  if (kpi.showEcart && kpi.proj != null && kpi.n1 != null && kpi.n1 !== 0) {
    const dCHF = kpi.proj - kpi.n1; const dPct = (dCHF / kpi.n1) * 100; const sg = dCHF >= 0 ? '+' : '−';
    ecart = t('phrases:p_29751d6655', {
      year: 2025,
      signedAmt: `${sg}${fmtNum(Math.abs(dCHF), div)}`,
      unit: unit === 'chf' ? 'CHF' : 'kCHF',
      signedPct: `${sg}${Math.abs(dPct).toFixed(0)}`,
    });
  }

  const chartTitle = kpi.chartTitle || CHART_TITLE[kpi.mode] || p('Historique mensuel');

  return (
    <div className={`ckdr ckpit__st--${kpi.status}`}>
      <div className="ckdr__topbar" />
      <div className="ckdr__pad">
        <div className="ckdr__head">
          <div>
            <div className="ckdr__name">{kpi.label}</div>
            <div className="ckdr__valrow">
              <span className="ckdr__value">{fmtKpiValue(kpi, unit)}</span>
              {kpi.type !== 'mois' && !kpi.valueOverride && <span className="ckdr__unit">{unitLabel(kpi, unit)}</span>}
            </div>
            <div className="ckdr__pill">{STATUS_LABEL[kpi.status]} · {kpi.delta}</div>
          </div>
          <button type="button" className="ckdr__close" onClick={onClose} aria-label={t('phrases:p_37e25cbf53')}>×</button>
        </div>

        <div className="ckdr__stats">
          {stats.map((s, i) => (
            <div className="ckdr__stat" key={i}>
              <div className="ckdr__stat-lbl">{s.lbl}</div>
              <div className="ckdr__stat-val">{s.val}</div>
            </div>
          ))}
        </div>

        {ecart && <div className="ckdr__ecart">{ecart}</div>}

        <div className="ckdr__section">
          <div className="ckdr__section-t">{chartTitle}</div>
          <KpiChart kpi={kpi} color="var(--color-accent)" div={div} />
        </div>

        <div className="ckdr__section">
          <div className="ckdr__section-t">{p('Ce qui le compose')}</div>
          {kpi.mix ? (
            <div className="ckdr__mix">
              <div className="ckdr__mixrow ckdr__mixhead">
                <span className="ckdr__mix-l" />
                <span className="ckdr__mix-v">{p('À ce jour')}</span>
                <span className="ckdr__mix-v">{p('Mix N-1')}</span>
              </div>
              {kpi.mix.map((d, i) => (
                <div className="ckdr__mixrow" key={i}>
                  <span className="ckdr__mix-l">{d.label}</span>
                  <span className="ckdr__mix-v">{fmtNum(d.val, div)}{d.pct != null && <em> · {d.pct.toFixed(0)}%</em>}</span>
                  <span className="ckdr__mix-v ckdr__mix-25">{d.val25 != null ? fmtNum(d.val25, div) : '—'}{d.pct25 != null && <em> · {d.pct25.toFixed(0)}%</em>}</span>
                </div>
              ))}
            </div>
          ) : kpi.drivers.map((d, i) => {
            const val = d[1];
            const neg = Number.isFinite(Number(val)) && Number(val) < 0;
            // % du CA net (base = driverBase) quand fourni : lecture de la structure.
            const pct = (kpi.driverBase && Number.isFinite(Number(val)) && Math.abs(kpi.driverBase) > 1e-9)
              ? Math.round((Number(val) / kpi.driverBase) * 100) : null;
            return (
              <div className="ckdr__driver" key={i}>
                <span className="ckdr__driver-l">{d[0]}</span>
                <span className={`ckdr__driver-v${neg ? ' neg' : ''}`}>{fmtDriver(val, kpi, unit)}{pct != null && <em className="ckdr__driver-pct"> · {pct} %</em>}</span>
              </div>
            );
          })}
        </div>

        {/* Chaque cible est une SECTION depuis l'accès direct aux états
            (2026-09-17) : le relais `reports_pending_tab`, qui demandait un
            onglet au hub Rapports, est retiré avec sa raison d'être. */}
        {kpi.target && (
          <div className="ckdr__cta" onClick={() => onGoTab(kpi.target)}>
            {t('phrases:p_b71b372954', { tab: kpi.tab })}
          </div>
        )}
      </div>
    </div>
  );
}

function buildStats(kpi, unit, p) {
  const raw = [
    [kpi.statLabels?.[0] || p('Réalisé'), fmtStat(kpi.ytd, kpi, unit)],
    [kpi.statLabels?.[1] || p('Projection'), fmtStat(kpi.proj, kpi, unit)],
    [kpi.statLabels?.[2] || p('Exercice précédent'), fmtStat(kpi.n1, kpi, unit)],
  ];
  const f = kpi.noProj ? [raw[0], raw[2]] : raw;
  return f.map((s) => ({ lbl: s[0], val: s[1] }));
}

/* Driver : montant chf (÷ unité) ou nombre brut (EPT, ratio) selon l'échelle. */
function fmtDriver(v, kpi, unit) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) < 1000) {
    const r = Math.round(n * 100) / 100;
    return (n < 0 ? '−' : '') + Math.abs(r).toString();
  }
  return fmtNum(n, unit === 'chf' ? 1 : 1000);
}
